import { debounce } from 'lodash/fp';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { WithChildren } from '@optimus-web/common';
import { MIDDLE_CONTAINER_WIDTH } from '../constants';

export const ResponsiveBreakpointContext = createContext(false);

const resizeDebounce = 200;

/**
 * This hook helps us support two design variants: one for desktop and one for mobile/tablet.
 *
 * Why only two? We always receive only two designs in Figma, so we shouldn't invent a third one.
 * It's better to stick to two versions rather than trying to use all available breakpoints.
 * We were doing this before, and it was hard to understand what’s going on.
 *
 * Having two good versions is better then not knowing what’s going on.
 */
export const useIsMobile = () => useContext(ResponsiveBreakpointContext);

const detectMobile = () => {
    const mobileDetected = window.matchMedia(`(min-width: 0px) and (max-width: ${MIDDLE_CONTAINER_WIDTH - 1}px)`);
    return mobileDetected.matches;
};

export const ResponsiveBreakpointProvider: FC<WithChildren> = ({ children }) => {
    const [isMobile, setIsMobile] = useState(detectMobile());

    const handleResize = debounce(resizeDebounce, () => {
        setIsMobile(detectMobile());
    });

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    return <ResponsiveBreakpointContext.Provider value={isMobile}>{children}</ResponsiveBreakpointContext.Provider>;
};
